import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedPrice from "./AnimatedPrice";
import QuoteTotals from "./QuoteTotals";
import { formatMoney, isEmptyQuote, SIZE_LABELS, REPAIR_LABELS } from "@/lib/pricing";
import { EXPAND } from "@/lib/motion";

export function PanelLine({ it, sym }) {
  if (it.kind === "imported" || (!it.en && it.description)) {
    return (
      <div className="flex justify-between items-start text-sm">
        <div className="min-w-0"><div className="font-medium truncate">{it.description}</div>{it.detail && <div className="text-[11px] text-slate-500 truncate">{it.detail}</div>}</div>
        <span className="font-mono-num font-semibold shrink-0 ml-2">{formatMoney(sym, it.total)}</span>
      </div>
    );
  }
  return (
    <div className="flex justify-between items-start text-sm">
      <div className="min-w-0">
        <div className="font-medium truncate">{it.en} {it.qty > 1 && <span className="font-mono-num text-amber-400 text-xs">×{it.qty}</span>} {it.zh && <span className="font-zh text-amber-300/60 text-xs">{it.zh}</span>}</div>
        <div className="text-[11px] text-slate-500 font-mono-num">
          {it.sprayMode === "touchup" ? <span className="text-sky-300">touch-up</span> : "respray"} {formatMoney(sym, it.respray)}
          {it.repair > 0 && <span className="text-emerald-400"> · {REPAIR_LABELS[it.repairTier]?.short || "repair"} +{formatMoney(sym, it.repair)}</span>}
          {it.special > 0 && <span className="text-violet-400"> · fx +{formatMoney(sym, it.special)}</span>}
        </div>
      </div>
      <span className="font-mono-num font-semibold shrink-0 ml-2">{formatMoney(sym, it.total)}</span>
    </div>
  );
}

export function ServiceLine({ s, sym }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-emerald-300">{s.en} <span className="font-zh text-xs text-amber-300/60">{s.zh}</span></span>
      <span className="font-mono-num font-semibold">{formatMoney(sym, s.price)}</span>
    </div>
  );
}

export function PartLine({ p, sym }) {
  return (
    <div className="flex justify-between items-start text-sm" data-testid={`part-line-${p.id}`}>
      <div className="min-w-0">
        <div className="text-sky-300 truncate">{p.name} <span className="font-zh text-xs text-amber-300/60">零件</span></div>
        <div className="text-[11px] text-slate-500 font-mono-num">part {formatMoney(sym, p.price)}{p.ri > 0 && <span> · R&I +{formatMoney(sym, p.ri)}</span>} · net</div>
      </div>
      <span className="font-mono-num font-semibold shrink-0 ml-2">{formatMoney(sym, p.total)}</span>
    </div>
  );
}

function LineItems({ quote, sym }) {
  return (
    <div className="p-5 space-y-3 max-h-[38vh] overflow-y-auto">
      <AnimatePresence initial={false}>
        {quote?.lineItems.map((it) => (
          <motion.div key={it.key} {...EXPAND}><PanelLine it={it} sym={sym} /></motion.div>
        ))}
        {quote?.serviceItems.map((s) => (
          <motion.div key={s.id} {...EXPAND}><ServiceLine s={s} sym={sym} /></motion.div>
        ))}
        {quote?.partItems?.map((p) => (
          <motion.div key={p.id} {...EXPAND}><PartLine p={p} sym={sym} /></motion.div>
        ))}
      </AnimatePresence>
      {isEmptyQuote(quote) && (
        <p className="text-sm text-slate-500 text-center py-6">Select panels to build a quote<br /><span className="font-zh text-xs">请选择面板以生成报价</span></p>
      )}
    </div>
  );
}

export default function QuoteSummary({ quote, sym, size, discount, setDiscount }) {
  return (
    <div className="bg-gradient-to-b from-[#1A2234] to-[#111827] rounded-2xl border border-amber-500/25 overflow-hidden shadow-xl shadow-black/40" data-testid="quote-breakdown-container">
      <div className="p-5 border-b border-slate-800">
        <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Estimated Total · 预估合计</p>
        <AnimatedPrice value={quote?.total || 0} symbol={sym} className="font-mono-num text-4xl sm:text-5xl font-black text-amber-400 tracking-tight block mt-1" />
        <p className="text-xs text-slate-500 mt-1 font-mono-num">
          {quote?.panelCount || 0} panels · {SIZE_LABELS[size].en}{quote?.manual ? "" : ` ×${quote?.mult}`}
          <span data-testid="total-price-display" className="hidden">{quote?.total || 0}</span>
        </p>
      </div>
      <LineItems quote={quote} sym={sym} />
      <QuoteTotals quote={quote} sym={sym} discount={discount} setDiscount={setDiscount} />
    </div>
  );
}
